import moment from 'moment';
import 'moment/locale/pt-br';

moment.locale('pt-br');

export type BankSlipFilterValues = {
  search: string;
  dueDate: string;
  competence: string;
  status: string;
};

export const SEARCH_MIN_LENGTH = 3;
export const COMPETENCE_HISTORY_MONTHS = 120;

export const normalizeCompetenceValue = (value: string): string => {
  const normalized = value.trim().replace('-', '/');
  const parts = normalized.split('/');

  if (parts.length !== 2) return normalized;

  if (parts[0].length === 4) {
    return `${parts[0]}/${parts[1].padStart(2, '0')}`;
  }

  if (parts[1].length === 4) {
    return `${parts[1]}/${parts[0].padStart(2, '0')}`;
  }

  return normalized;
};

export type BankSlipApiFilters = {
  typeableLineSearch: string | null;
  totalAmount: string | null;
  beginDate: string | null;
  endDate: string | null;
  status: string | null;
  competence: string | null;
};

const STATUS_TO_URL: Record<string, string> = {
  a_vencer: 'A vencer',
  vencido: 'Vencido',
  pago: 'Pagos',
};

const STATUS_FROM_URL: Record<string, string> = {
  'A vencer': 'a_vencer',
  Vencido: 'vencido',
  Pagos: 'pago',
};

export const getCompetenceOptionLabel = (value: string): string => {
  const normalized = normalizeCompetenceValue(value);
  const parsed = moment(normalized, 'YYYY/MM', true);

  if (!parsed.isValid()) return value;

  const label = parsed.format('MMMM/YYYY');

  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getCompetenceOptions = (
  selectedCompetence = ''
): Array<{ value: string; label: string }> => {
  const options = Array.from({ length: COMPETENCE_HISTORY_MONTHS }, (_, index) => {
    const month = moment().subtract(index, 'months');

    return {
      value: month.format('YYYY/MM'),
      label: getCompetenceOptionLabel(month.format('YYYY/MM')),
    };
  });

  const normalizedSelected = selectedCompetence
    ? normalizeCompetenceValue(selectedCompetence)
    : '';

  if (
    normalizedSelected &&
    !options.some((option) => option.value === normalizedSelected)
  ) {
    options.push({
      value: normalizedSelected,
      label: getCompetenceOptionLabel(normalizedSelected),
    });
    options.sort((first, second) => second.value.localeCompare(first.value));
  }

  return options;
};

export const formatDueDateInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

export const parseDueDateToIso = (value: string): string | null => {
  if (value.length !== 10) return null;

  const parsed = moment(value, 'DD/MM/YYYY', true);

  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

export const formatIsoToDueDate = (value: string | null): string => {
  if (!value) return '';

  const parsed = moment(value, 'YYYY-MM-DD', true);

  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

const normalizeAmount = (value: string): string | null => {
  const normalized = value
    .replace(/R\$\s?/gi, '')
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;

  return Number(normalized).toFixed(2);
};

export const parseSearchTerm = (
  value: string
): Pick<BankSlipApiFilters, 'typeableLineSearch' | 'totalAmount'> => {
  const trimmed = value.trim();

  if (!trimmed) {
    return { typeableLineSearch: null, totalAmount: null };
  }

  const looksLikeAmount =
    /R\$/i.test(trimmed) || trimmed.includes(',') || /^\d+[.,]\d{1,2}$/.test(trimmed);

  if (looksLikeAmount) {
    const amount = normalizeAmount(trimmed);

    if (amount) {
      return {
        typeableLineSearch: null,
        totalAmount: amount,
      };
    }
  }

  return {
    typeableLineSearch: trimmed.replace(/\D/g, ''),
    totalAmount: null,
  };
};

export const isSearchReadyToApply = (search: string): boolean => {
  const trimmed = search.trim();

  if (!trimmed) return true;

  const parsed = parseSearchTerm(trimmed);

  if (parsed.totalAmount) return true;

  return (parsed.typeableLineSearch?.length ?? 0) >= SEARCH_MIN_LENGTH;
};

export const isSearchPending = (input: string, applied: string): boolean => {
  const trimmedInput = input.trim();
  const trimmedApplied = applied.trim();

  if (!trimmedInput || trimmedInput === trimmedApplied) return false;

  return !isSearchReadyToApply(trimmedInput);
};

const buildSearchDisplay = (params: URLSearchParams): string => {
  const searchQuery = params.get('busca');

  if (searchQuery) return searchQuery;

  return '';
};

export const readFiltersFromSearch = (search: string): BankSlipFilterValues => {
  const params = new URLSearchParams(search);
  const beginDate = params.get('begin-date');
  const endDate = params.get('end-date');
  const status = params.get('status');

  return {
    search: buildSearchDisplay(params),
    dueDate:
      beginDate && endDate && beginDate === endDate
        ? formatIsoToDueDate(beginDate)
        : formatIsoToDueDate(beginDate),
    competence: params.get('competence') || '',
    status: status ? STATUS_FROM_URL[status] || '' : '',
  };
};

export const readApiFiltersFromSearch = (search: string): BankSlipApiFilters => {
  const params = new URLSearchParams(search);
  const searchValues = parseSearchTerm(buildSearchDisplay(params));

  return {
    typeableLineSearch: searchValues.typeableLineSearch,
    beginDate: params.get('begin-date'),
    endDate: params.get('end-date'),
    status: params.get('status'),
    totalAmount: searchValues.totalAmount,
    competence: params.get('competence'),
  };
};

export const buildSearchFromFilters = (filters: BankSlipFilterValues): string => {
  const params = new URLSearchParams();
  const dueDateIso = parseDueDateToIso(filters.dueDate);

  if (filters.search.trim()) {
    params.set('busca', filters.search.trim());
  }

  if (dueDateIso) {
    params.set('begin-date', dueDateIso);
    params.set('end-date', dueDateIso);
  }

  if (filters.competence) {
    params.set('competence', filters.competence);
  }

  if (filters.status && STATUS_TO_URL[filters.status]) {
    params.set('status', STATUS_TO_URL[filters.status]);
  } else if (dueDateIso) {
    params.set('status', 'Todos');
  }

  return params.toString();
};

export const hasActiveFilters = (search: string): boolean => {
  const filters = readApiFiltersFromSearch(search);

  return Boolean(
    filters.typeableLineSearch ||
      filters.totalAmount ||
      (filters.beginDate && filters.endDate) ||
      filters.status ||
      filters.competence
  );
};

export const matchesTypeableLine = (
  typeableLine: string | undefined,
  search: string
): boolean => {
  if (!typeableLine || !search) return false;

  const normalizedLine = typeableLine.replace(/\D/g, '');
  const normalizedSearch = search.replace(/\D/g, '');

  return normalizedLine.includes(normalizedSearch);
};

export const matchesInstallmentAmount = (
  installment: { amountOriginal?: number | string },
  invoice: { totalAmount?: number | string },
  amount: string
): boolean => {
  const target = Number(amount).toFixed(2);

  if (installment.amountOriginal !== undefined && installment.amountOriginal !== null) {
    return Number(installment.amountOriginal).toFixed(2) === target;
  }

  if (invoice.totalAmount !== undefined && invoice.totalAmount !== null) {
    return Number(invoice.totalAmount).toFixed(2) === target;
  }

  return false;
};

export const matchesDueDate = (
  dueDate: string,
  beginDate: string,
  endDate: string
): boolean => {
  const installmentDueDate = moment(dueDate).startOf('day');
  const filterBeginDate = moment(beginDate, 'YYYY-MM-DD').startOf('day');
  const filterEndDate = moment(endDate, 'YYYY-MM-DD').startOf('day');

  return (
    installmentDueDate.isSameOrAfter(filterBeginDate, 'day') &&
    installmentDueDate.isSameOrBefore(filterEndDate, 'day')
  );
};

export const getInstallmentCompetence = (
  installment: { referenceMonth?: string },
  invoice: { emissionDate?: string },
  customerType?: string | null
): string | null => {
  const type =
    customerType ??
    (typeof window !== 'undefined'
      ? localStorage.getItem('@UltragazEmpresarial:customerType')
      : null);

  if (type === 'MI' && installment.referenceMonth) {
    return normalizeCompetenceValue(installment.referenceMonth);
  }

  if (invoice.emissionDate) {
    return moment(invoice.emissionDate).format('YYYY/MM');
  }

  if (installment.referenceMonth) {
    return normalizeCompetenceValue(installment.referenceMonth);
  }

  return null;
};

export const getCompetenceDateRange = (
  competence: string
): { beginDate: string; endDate: string } | null => {
  const normalized = normalizeCompetenceValue(competence);
  const parsed = moment(normalized, 'YYYY/MM', true);

  if (!parsed.isValid()) return null;

  return {
    beginDate: parsed.clone().startOf('month').format('YYYY-MM-DD'),
    endDate: parsed.clone().endOf('month').format('YYYY-MM-DD'),
  };
};

export const matchesCompetence = (
  installment: { referenceMonth?: string },
  invoice: { emissionDate?: string },
  competence: string,
  customerType?: string | null
): boolean => {
  const installmentCompetence = getInstallmentCompetence(
    installment,
    invoice,
    customerType
  );

  if (!installmentCompetence) return false;

  return (
    installmentCompetence === normalizeCompetenceValue(competence)
  );
};
